import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, Button, Row, Col, Input, InputGroup, Label } from 'reactstrap';
import { useDispatch } from 'react-redux';
import { updateUserOperator, fetchAllUsers } from '../../slices/users/thunk';
import { updateOperator, getOperators } from '../../slices/operators/thunk';

const WinMultiplierModal = ({ isOpen, toggle, target, gameType, isOperator = false }) => {
    const dispatch = useDispatch();
    const [customValue, setCustomValue] = useState('');
    const [activeCustomGame, setActiveCustomGame] = useState(null);
    const [loading, setLoading] = useState(false);
    const [currentMultipliers, setCurrentMultipliers] = useState({});

    // Common multipliers as per dashboard UI
    const commonMultipliers = [5, 10, 20, 50, 100];

    useEffect(() => {
        if (isOpen && target) {
            setCurrentMultipliers(target.winMultiplier || {});
        } else if (!isOpen) {
            setActiveCustomGame(null);
            setCustomValue('');
        }
    }, [isOpen, target]);

    const handleUpdate = async (game, value) => {
        setLoading(true);
        try {
            const extraKeys = {};
            if (game === 'lt2') extraKeys.lt3 = value;
            if (game === 'lt3') extraKeys.lt2 = value;
            const updateData = { winMultiplier: { ...currentMultipliers, [game]: value, ...extraKeys } };

            let result;
            if (isOperator) {
                result = await dispatch(updateOperator({ id: target._id, data: updateData }));
            } else {
                result = await dispatch(updateUserOperator(target._id, updateData));
            }

            if (result.success || (result.payload && (result.payload.success || result.payload.status === 1))) {
                // Update local state immediately
                setCurrentMultipliers(updateData.winMultiplier);

                if (!isOperator) {
                    dispatch(fetchAllUsers({ gameType }));
                    toggle();
                } else {
                    dispatch(getOperators({ page: 1, limit: 10 }));
                }
            }
        } catch (error) {
            console.error("Error updating winMultiplier:", error);
        } finally {
            setLoading(false);
        }
    };

    const renderMultiplierSection = (game, label) => {
        const currentValue = currentMultipliers?.[game] ?? (game === 'lt2' ? currentMultipliers?.['lt3'] : (game === 'lt3' ? currentMultipliers?.['lt2'] : undefined));
        const isCustom = currentValue && !commonMultipliers.includes(currentValue);

        return (
            <div className="game-settings-section mb-3 p-3 rounded bg-light border" key={game}>
                <h5 className="text-primary border-bottom pb-2 mb-3 d-flex align-items-center" style={{ fontSize: '15px' }}>
                    <i className="ri-gamepad-line me-2"></i> {label}
                </h5>

                <div className="multiplier-section">
                    <Label className="fw-bold text-uppercase fs-11 mb-2 text-muted">Win Multiplier (Static)</Label>
                    <div className="d-flex flex-wrap gap-2 align-items-center">
                        {commonMultipliers.map((mult) => (
                            <Button
                                key={mult}
                                color="none"
                                className={`multiplier-btn ${currentValue === mult ? 'active' : ''}`}
                                onClick={() => {
                                    handleUpdate(game, mult);
                                    setActiveCustomGame(null);
                                }}
                                disabled={loading}
                                style={{ padding: '5px 10px', fontSize: '12px', minWidth: '45px' }}
                            >
                                {mult}x
                            </Button>
                        ))}

                        {/* Show custom active multiplier button if it is custom */}
                        {isCustom && (
                            <Button
                                color="none"
                                className="multiplier-btn active"
                                onClick={() => {
                                    setActiveCustomGame(game);
                                    setCustomValue(currentValue);
                                }}
                                disabled={loading}
                                style={{ padding: '5px 10px', fontSize: '12px', minWidth: '45px' }}
                            >
                                {currentValue}x
                            </Button>
                        )}

                        <Button
                            color="none"
                            className={`multiplier-btn custom-btn ${activeCustomGame === game ? 'active' : ''}`}
                            onClick={() => {
                                if (activeCustomGame === game) {
                                    setActiveCustomGame(null);
                                } else {
                                    setActiveCustomGame(game);
                                    setCustomValue(isCustom ? currentValue : '');
                                }
                            }}
                            disabled={loading}
                            style={{ padding: '5px 10px', fontSize: '12px', minWidth: '45px' }}
                        >
                            + Custom
                        </Button>

                        <Button
                            color="none"
                            className={`multiplier-btn ${currentValue === null || currentValue === undefined ? 'active' : ''}`}
                            style={currentValue === null || currentValue === undefined ? { padding: '5px 10px', fontSize: '12px', minWidth: '45px' } : { borderColor: '#f06548', color: '#f06548', padding: '5px 10px', fontSize: '12px', minWidth: '45px' }}
                            onClick={() => {
                                handleUpdate(game, null);
                                setActiveCustomGame(null);
                            }}
                            disabled={loading}
                        >
                            None
                        </Button>
                    </div>

                    {/* Inline Custom Input Group */}
                    {activeCustomGame === game && (
                        <div className="mt-3 p-2 bg-white rounded border d-flex gap-2 align-items-center">
                            <Input
                                type="number"
                                size="sm"
                                placeholder="Multiplier e.g. 15"
                                value={customValue}
                                onChange={(e) => setCustomValue(e.target.value)}
                                style={{ height: '30px', fontSize: '12px' }}
                                autoFocus
                            />
                            <Button
                                color="success"
                                size="sm"
                                onClick={() => {
                                    const val = parseInt(customValue, 10);
                                    if (val && val > 0) {
                                        handleUpdate(game, val);
                                        setActiveCustomGame(null);
                                        setCustomValue('');
                                    }
                                }}
                                style={{ height: '30px', padding: '0 10px', fontSize: '12px', fontWeight: 'bold' }}
                            >
                                Apply
                            </Button>
                            <Button
                                color="light"
                                size="sm"
                                onClick={() => setActiveCustomGame(null)}
                                style={{ height: '30px', padding: '0 10px', fontSize: '12px' }}
                            >
                                Cancel
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="md" className="multiplier-modal mt-4">
            <ModalHeader toggle={toggle} className="bg-light py-2">
                <span className="fw-bold fs-15">Set Win Multipliers</span>
            </ModalHeader>
            <ModalBody className="p-3">
                <style>{`
                    .multiplier-btn {
                        padding: 8px 16px;
                        border-radius: 6px;
                        border: 2px solid #dee2e6;
                        background-color: #f8f9fa;
                        color: #333;
                        font-weight: 600;
                        font-size: 14px;
                        cursor: pointer;
                        transition: all 0.2s ease;
                        min-width: 60px;
                    }
                    .multiplier-btn:hover {
                        border-color: #0d9582;
                        background-color: #0d9582;
                        color: #fff;
                    }
                    .multiplier-btn.active {
                        background-color: #0d9582;
                        border-color: #0d9582;
                        color: #fff;
                    }
                    .multiplier-btn.custom-btn {
                        background-color: #e7f6ff;
                        border-color: #0d9582;
                        color: #0d9582;
                    }
                    .multiplier-btn.custom-btn:hover {
                        background-color: #0d9582;
                        color: #fff;
                    }
                    .multiplier-btn.custom-btn.active {
                        background-color: #0d9582;
                        border-color: #0d9582;
                        color: #fff;
                    }
                `}</style>
                <div className="text-center mb-3 p-2 bg-primary-subtle rounded">
                    <h6 className="mb-0 text-primary fs-13">Account: {target?.acnumber || target?.username}</h6>
                    <p className="text-muted small mb-0 fs-11">Role: <span className="text-uppercase fw-bold">{isOperator ? 'Operator' : 'User'}</span></p>
                </div>

                {gameType ? (
                    renderMultiplierSection(
                        gameType,
                        gameType === 'lt' ? 'Lucky Target' :
                        gameType === 'lt1' ? 'Lucky Target 1' :
                        (gameType === 'lt2' || gameType === 'lt3') ? 'Lucky Target 2' :
                        gameType === 'ro' ? 'American Roulette' : 'Sorat'
                    )
                ) : (
                    <>
                        {renderMultiplierSection('lt', 'Lucky Target')}
                        <hr className="my-2 opacity-50" />
                        {renderMultiplierSection('lt1', 'Lucky Target 1')}
                        <hr className="my-2 opacity-50" />
                        {renderMultiplierSection('lt2', 'Lucky Target 2')}
                        <hr className="my-2 opacity-50" />
                        {renderMultiplierSection('ro', 'Roulette')}
                        <hr className="my-2 opacity-50" />
                        {renderMultiplierSection('so', 'Sorat')}
                    </>
                )}
            </ModalBody>
        </Modal>
    );
};

export default WinMultiplierModal;
